<template>
  <div class="cash-call-co-lead-step">
    <div ref="searchWrap" class="co-lead-search">
      <label>Claim Reference <span class="text-danger">*</span></label>
      <b-input-group>
        <b-form-input v-model="searchQuery" placeholder="e.g. BRIT-0123" autocomplete="off" />
        <b-input-group-append>
          <BaseButton type="button" size="sm">Search</BaseButton>
        </b-input-group-append>
      </b-input-group>

      <!-- Messages sit directly under the search input (top of the results) so
           they're always visible without scrolling past a long results list. -->
      <div v-if="duplicateError" class="co-lead-search__msg co-lead-search__msg--error mt-2">
        This UMR/key combination has already been added.
      </div>
      <div v-if="claimRefError" class="co-lead-search__msg co-lead-search__msg--brand mt-2">
        {{ claimRefError }}
      </div>
      <div v-else-if="lockedClaimRef" class="co-lead-search__msg co-lead-search__msg--brand-soft mt-2">
        Adding UMRs for Claim Ref <strong>{{ lockedClaimRef }}</strong>. All co-lead UMRs must share this claim reference.
      </div>

      <b-table-simple v-if="searchQuery" small hover class="co-lead-search__results mt-2">
        <b-thead>
          <b-tr>
            <b-th>LimeIQ Key</b-th>
            <b-th>Claim Ref</b-th>
            <b-th>Underwriter</b-th>
            <b-th>Broker</b-th>
            <b-th></b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr v-if="!searchResults.length">
            <b-td colspan="5" class="co-lead-search__empty-cell">
              <template v-if="lockedClaimRef && searchQuery.trim().toLowerCase() !== lockedClaimRef.toLowerCase()">
                Only UMRs for Claim Ref <strong>{{ lockedClaimRef }}</strong> can be added. If one is missing, use “+ Add missing UMR” below.
              </template>
              <template v-else-if="lockedClaimRef">
                All UMRs for Claim Ref <strong>{{ lockedClaimRef }}</strong> are already added. Use “+ Add missing UMR” below to add one that isn’t in the system.
              </template>
              <template v-else>
                No matches — use “+ Add missing UMR” below.
              </template>
            </b-td>
          </b-tr>
          <b-tr v-for="(result, index) in pagedSearchResults" :key="index">
            <!-- LIQ-2414: the LimeIQ Key, not the bare UMR - one UMR can carry
                 several keys (sections, risk codes...), and each is selected
                 on its own. -->
            <b-td>{{ bdxKeyOfResult(result) }}</b-td>
            <b-td>{{ result.claim_ref || '—' }}</b-td>
            <b-td>{{ result.underwriter || '—' }}</b-td>
            <b-td>{{ result.broker || '—' }}</b-td>
            <b-td>
              <BaseButton type="button" size="sm" @click="selectResult(result)">Select</BaseButton>
            </b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>

      <!-- Pagination for the search results. Only shown when the results span
           more than one page. Uses the platform's b-pagination pattern; the
           wrapper keeps it right-aligned and clear of the table so its text
           never overlaps adjacent content. -->
      <div
          v-if="searchResults.length > searchPageSize"
          class="co-lead-search__pagination"
      >
        <b-pagination
            v-model="searchPage"
            :per-page="searchPageSize"
            :total-rows="searchResults.length"
            size="sm"
            align="right"
            class="mb-0"
        />
      </div>
    </div>

    <p class="co-lead-umrs__heading mt-3">Co-Lead UMRs — Share Allocation</p>

    <div v-if="rows.length" class="co-lead-umrs__table-wrap">
    <b-table-simple small hover responsive class="co-lead-umrs__table">
      <b-thead>
        <b-tr>
          <b-th>Type</b-th>
          <b-th>LimeIQ Key</b-th>
          <b-th>Section #</b-th>
          <b-th>Risk Code</b-th>
          <b-th>CAT Code</b-th>
          <b-th>CCY</b-th>
          <b-th>Claim Ref</b-th>
          <b-th>Underwriter</b-th>
          <b-th>Broker</b-th>
          <b-th>
            Funding Required
            <b-icon icon="info-circle" v-b-tooltip.hover.viewport="'Off = this UMR does not need funding'" />
          </b-th>
          <b-th>Share %</b-th>
          <b-th></b-th>
        </b-tr>
      </b-thead>
      <b-tbody>
        <!-- Every selected UMR is listed in this one table - no pagination. A
             co-lead typically has 4-5 UMRs (10 is a rare edge case); beyond
             5 rows the table scrolls vertically (.co-lead-umrs__table-wrap)
             with its headings pinned. `index` is the row's
             position in `rows`, which is what remove / edit / removeBroker
             act on. -->
        <template v-for="(row, index) in rows">
        <b-tr :key="row.bdx_key[0]">
          <b-td><span class="co-lead-umrs__type-badge" :class="{ 'co-lead-umrs__type-badge--manual': !row.matched }">{{ row.matched ? 'LIQ' : 'Manual' }}</span></b-td>

          <!-- Two kinds of row:
               - FROM THE CLAIM REF SEARCH (row.matched === true, "LIQ"): read-only,
                 its values come from the matching system key.
               - MANUALLY ADDED (row.matched === false, "Manual"): edited through
                 the Add Missing UMR modal via the row's pencil.
               `row.matched` is the discriminator, NOT the row position.

               Every cell is read-only: editing is modal-only, so the pencil
               re-opens the Add Missing UMR modal pre-filled with this row and
               replaces it on save. -->
          <!-- LimeIQ Key (LIQ-2414: shown instead of the bare UMR). Manual rows
               carry the key built from their typed fields, so this is filled
               for both LIQ and Manual rows. -->
          <b-td>{{ (row.bdx_key && row.bdx_key[0]) || row.umr }}</b-td>
          <!-- Section # -->
          <b-td>{{ row.sn }}</b-td>
          <!-- Risk Code -->
          <b-td>{{ row.rc }}</b-td>
          <!-- CAT Code -->
          <b-td>{{ row.cc }}</b-td>
          <!-- CCY -->
          <b-td>{{ row.currency_type }}</b-td>
          <!-- Claim Ref -->
          <b-td>{{ (row.claimref && row.claimref[0]) || '—' }}</b-td>
          <!-- Underwriter -->
          <b-td>{{ row.underwriter_lead }}</b-td>
          <!-- Broker -->
          <b-td>
            <div class="co-lead-brokers">
              <span
                  v-for="(b, bIndex) in brokerList(row)"
                  :key="bIndex"
                  class="co-lead-broker-chip"
              >
                {{ b }}
                <button
                    v-if="!isCarriedPrimaryBroker(row, bIndex)"
                    type="button"
                    class="co-lead-broker-chip__remove"
                    aria-label="Remove broker"
                    @click="removeBroker(index, bIndex)"
                >×</button>
              </span>
            </div>
          </b-td>

          <b-td>
            <BaseSwitch v-model="row.funding_required" @input="emitUpdate" />
          </b-td>
          <b-td>
            <div class="co-lead-umrs__share">
              <BaseInput
                  v-model.number="row.share_percentage"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  size="sm"
                  @input="emitUpdate"
              />
            </div>
          </b-td>
          <b-td class="co-lead-umrs__remove-cell">
            <!-- The pencil is only for MANUAL rows, and it opens the Add Missing
                 UMR modal pre-filled with this row (editing is modal-only - there
                 is no inline editing in this table). Rows entered from the Claim
                 Reference search (row.matched / LIQ) carry system values, so they
                 have NO pencil - just the remove (x). -->
            <!-- Reserve the pencil slot even on LIQ rows (which have no
                 pencil) with an empty placeholder, so the x always lands in
                 the same horizontal position across every row. -->
            <button
                v-if="!row.matched"
                type="button"
                class="co-lead-umrs__edit"
                aria-label="Edit UMR"
                v-b-tooltip.hover.viewport="'Edit'"
                @click="openEditModal(row, index)"
            >
              <b-icon icon="pencil" />
            </button>
            <span v-else class="co-lead-umrs__edit co-lead-umrs__edit--placeholder" aria-hidden="true"></span>
            <button
                type="button"
                class="co-lead-umrs__remove"
                aria-label="Remove UMR"
                v-b-tooltip.hover.viewport="'Remove'"
                @click="removeRow(index)"
            >
              <b-icon icon="x" />
            </button>
          </b-td>
        </b-tr>
        </template>
      </b-tbody>
    </b-table-simple>
    </div>

    <div v-else class="text-muted mb-3">No UMRs added yet.</div>

    <CashCallAddMissingUmrModal
        ref="addMissingUmrModal"
        :existing-keys="existingBdxKeys"
        :locked-claim-ref="lockedClaimRef"
        :first-record="firstRecord"
        :shared-currency="sharedSystemCurrency"
        :currency-locked="currencyLocked"
        :underwriter-options="underwriterOptions"
        :broker-options="brokerOptions"
        :is-tpa-user="isTpaUser"
        :tpa-company-name="tpaCompanyName"
        :flattened-rows="searchIndex.rows"
        @save="onAddKey"
        @update="onUpdateKey"
        @redirect-to-claim-search="focusClaimSearch"
    />

    <div class="d-flex justify-content-end mt-3">
      <span class="co-lead-umrs__share-total" :class="{ 'text-danger': shareTotal !== 100, 'co-lead-umrs__share-total--complete': shareTotal === 100 }">
        Share total: {{ shareTotal }}% <b-icon v-if="shareTotal === 100" icon="check-circle-fill" />
      </span>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount, nextTick, getCurrentInstance } from '@vue/composition-api'
import { debounce } from 'lodash-es'
import { userMixin } from '@mixins'
import { totalSharePercentage, isDuplicateBdxKey } from '../../util/cashCallCoLead'
import { BaseInput, BaseButton, BaseSwitch } from '@components/base'
import CashCallService from '../../services/CashCallService'
import { LincolnRLSGroupsService } from '@services'
import CashCallAddMissingUmrModal from './CashCallAddMissingUmrModal.vue'

export default defineComponent({
  name: 'CashCallCoLeadStep',
  mixins: [userMixin],
  components: {
    BaseInput,
    BaseButton,
    BaseSwitch,
    CashCallAddMissingUmrModal
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    groupId: {
      type: [Number, String],
      default: null
    },
    // Prebuilt { rows, blobs } search index from the parent (built once when
    // the bulk cache is fetched, so it survives leaving/returning to this step
    // instead of being rebuilt over ~400k rows on every revisit).
    searchIndex: {
      type: Object,
      default: () => ({ rows: [], blobs: [] })
    }
  },
  emits: ['input'],
  setup(props, { emit }) {
    const rows = computed(() => props.value)

    const existingBdxKeys = computed(() => rows.value.map((row) => row.bdx_key[0]))

    const shareTotal = computed(() => totalSharePercentage(rows.value))

    // A co-lead cash call is for ONE claim, so every added UMR must carry the
    // same Claim Ref. The first added row fixes the claim ref; later rows may
    // differ in Underwriter/Broker/etc. but NOT in claim ref.
    const claimRefOf = (row) => {
      const cr = Array.isArray(row.claimref) ? row.claimref[0] : row.claimref
      return String(cr || '').trim()
    }
    const lockedClaimRef = computed(() =>
      rows.value.length ? claimRefOf(rows.value[0]) : ''
    )
    const claimRefError = ref('')

    // The first added row's shared attributes, used to pre-fill a manually-added
    // ("Add missing UMR") key when the underwriter is LimeIQ-active ("Yes"):
    //  - parties (TPA/Broker/Coverholder): a co-lead shares these, so a new UMR
    //    under the same claim inherits them (editable).
    //  - claim location (country/state/sl-state/province): same claim, so the
    //    new UMR starts with the same location, making its Claim Details tab
    //    render the full structure (Loss State / SL Tax State) immediately
    //    instead of an empty country. Still editable per UMR on step 3.
    //  - insured name: a co-lead is ONE claim, so the insured is the same across
    //    every UMR - inherit it from the first (system-sourced) record so the
    //    manual key's summary isn't blank.
    // Broker is stored as an array; take the first for the single-value field.
    const firstRecord = computed(() => {
      const first = rows.value[0]
      const empty = {
        exists: false,
        fromSystem: false,
        tpa: '', broker: '', coverholder: '', underwriter: '', currency_type: '',
        loss_country: '', loss_state: '', loss_sl_state: '', canada_province: '',
        naic_code: '', insured_name: '', cause_of_loss: '',
        // Key-identity fields carried so Add Missing UMR can prefill everything
        // EXCEPT the UMR from the first claim ref (per LIQ feedback).
        risk_code: '', cat_code: '', section_no: '', year_of_account: ''
      }
      if (!first) return empty
      const broker = Array.isArray(first.broker) ? (first.broker[0] || '') : (first.broker || '')
      return {
        exists: true,
        // True when the FIRST co-lead UMR was pulled from the system (a LIQ /
        // matched row, i.e. a Claim Reference was searched and UMRs loaded).
        // The shared parties (TPA/Coverholder/Broker) are then carried from it
        // and locked when adding further UMRs. If the first UMR was itself added
        // manually (matched === false), the parties stay editable.
        fromSystem: first.matched === true,
        tpa: first.tpa || '',
        broker,
        coverholder: first.coverholder || '',
        underwriter: first.underwriter_lead || '',
        currency_type: first.currency_type || '',
        loss_country: first.loss_country || '',
        loss_state: first.loss_state || '',
        loss_sl_state: first.loss_sl_state || '',
        canada_province: first.canada_province || '',
        naic_code: first.naic_code || '',
        insured_name: first.insured_name || '',
        // Shared Cause of Loss (a Step 3 claim-level field) so a manually-added
        // UMR inherits it too.
        cause_of_loss: first.cause_of_loss || '',
        // Key-identity fields from the first claim ref selected, so Add Missing
        // UMR can prefill Risk Code / CAT Code / Section # / Year of Account
        // (everything except the UMR itself). Row uses rc/cc/sn shorthands.
        risk_code: first.rc || '',
        cat_code: first.cc || '',
        section_no: first.sn || '',
        year_of_account: first.year_of_account || ''
      }
    })

    // Full distinct list of LEAD underwriters in the system (Lincoln's
    // get/lead-underwriters/ via our proxy), offered by the "Yes" flow's Lead
    // Underwriter dropdown. Fetched once when the step mounts.
    const underwriterOptions = ref([])
    onMounted(async () => {
      try {
        const resp = await CashCallService.getLeadUnderwriters()
        const names =
          (resp.data && resp.data.data && resp.data.data.Underwriter_Name) ||
          (resp.data && resp.data.Underwriter_Name) ||
          []
        underwriterOptions.value = Array.isArray(names) ? names : []
      } catch (e) {
        console.error('Failed to load lead underwriters', e)
        underwriterOptions.value = []
      }
    })

    // Distinct broker names in the system (Lincoln RLS 'Broker' groups), used to
    // autocomplete the "+ add" broker input on each co-lead row. Fetched once.
    const brokerOptions = ref([])
    onMounted(async () => {
      try {
        const resp = await LincolnRLSGroupsService.list('Broker')
        const list = (resp.data && resp.data.data) || []
        brokerOptions.value = Array.isArray(list)
          ? [...new Set(
              list
                .map((item) => (item && item.broker) || '')
                // Drop empties and the "*New Broker*" placeholder the RLS list
                // includes - it isn't a real broker to add here.
                .filter((b) => b !== '' && !/^\*.*\*$/.test(String(b).trim()))
            )]
          : []
      } catch (e) {
        console.error('Failed to load brokers', e)
        brokerOptions.value = []
      }
    })

    // Suggestions for the currently-open broker input: the full system broker
    // list (same source as the normal cash call broker dropdown), excluding
    // brokers already on that row, narrowed by what's typed. The dropdown is
    // scrollable, so we show the whole matching list rather than truncating it.
    const brokerSuggestions = computed(() => {
      if (addingBrokerFor.value === null) return []
      const query = String(newBroker.value || '').trim().toLowerCase()
      const already = brokerList(rows.value[addingBrokerFor.value] || {})
        .map((b) => String(b).trim().toLowerCase())
      return brokerOptions.value
        .filter((name) => !already.includes(String(name).trim().toLowerCase()))
        .filter((name) => (query ? String(name).toLowerCase().includes(query) : true))
    })

    const emitUpdate = () => {
      emit('input', rows.value)
    }

    // Returns true if the key was added, false if rejected (claim-ref mismatch).
    // Both the search-result "Select" and the Add-missing-UMR modal funnel here,
    // so the one-claim rule is enforced for every add path.
    const onAddKey = (row) => {
      const locked = lockedClaimRef.value
      if (locked && claimRefOf(row) !== locked) {
        claimRefError.value =
          `All co-lead UMRs must share the same Claim Ref (${locked}). ` +
          `This UMR's Claim Ref (${claimRefOf(row) || '—'}) is different and was not added.`
        return false
      }
      claimRefError.value = ''

      // Pass first-key details through to a MANUAL key (matched === false) when
      // that key left a shared field blank. A co-lead shares Broker / TPA /
      // Coverholder across keys, so a manually-added UMR inherits them from the
      // first key rather than showing blank on Step 3. A key already in LimeIQ
      // (matched === true) keeps its own resolved values untouched.
      const first = rows.value[0]
      let next = row
      if (first && row && row.matched === false) {
        const firstBroker = Array.isArray(first.broker) ? first.broker : (first.broker ? [first.broker] : [])
        const rowBroker = Array.isArray(row.broker) ? row.broker : (row.broker ? [row.broker] : [])
        next = {
          ...row,
          broker: rowBroker.length ? rowBroker : firstBroker,
          tpa: String(row.tpa || '').trim() ? row.tpa : (first.tpa || ''),
          coverholder: String(row.coverholder || '').trim() ? row.coverholder : (first.coverholder || ''),
        }
      }

      emit('input', [...rows.value, next])
      return true
    }

    const removeRow = (index) => {
      const next = rows.value.slice()
      next.splice(index, 1)
      emit('input', next)
    }

    // ---- Per-row broker chips (multiple brokers per key) ----
    // A key's broker is an array. A LIQ (matched) key's FIRST broker is carried
    // from the UMR and locked; the user can add more brokers and remove any of
    // the non-primary ones.
    const addingBrokerFor = ref(null)
    const newBroker = ref('')

    const brokerList = (row) => {
      if (Array.isArray(row.broker)) return row.broker.filter((b) => b !== null && b !== undefined && String(b).trim() !== '')
      if (row.broker === null || row.broker === undefined || String(row.broker).trim() === '') return []
      return [row.broker]
    }

    // Whether a broker chip is the CARRIED primary broker and so must be locked
    // (no remove "x"). A co-lead shares one primary broker across keys, carried
    // from the first UMR (the one from the Claim Ref search). The first chip
    // (bIndex 0) is that carried broker when EITHER the row came from the system
    // (matched/LIQ) OR its first broker matches the first record's carried
    // broker - which is exactly the "Add missing UMR + Yes" prefill case. A
    // brand-new underwriter ("No") that typed its own broker isn't carried, so
    // its chip stays removable.
    const isCarriedPrimaryBroker = (row, bIndex) => {
      if (bIndex !== 0) return false
      if (row.matched) return true
      // A brand-new broker entered via Add Missing UMR (broker_limeiq_active
      // false) is that manual UMR's own primary broker - it was set deliberately
      // in the modal, so its chip is locked (no remove "x") just like a carried
      // broker.
      if (row.broker_limeiq_active === false) return true
      const carried = String((firstRecord.value && firstRecord.value.broker) || '').trim().toLowerCase()
      if (!carried) return false
      const first = String(brokerList(row)[0] || '').trim().toLowerCase()
      return !!first && first === carried
    }

    const writeBrokers = (index, brokers) => {
      const next = rows.value.slice()
      next[index] = { ...next[index], broker: brokers }
      emit('input', next)
    }

    const instance = getCurrentInstance()

    // Called when the Add-Missing-UMR modal detects the entered UMR is already
    // in the system: it closes itself and asks us to send the user to the Claim
    // Reference search (that UMR isn't "missing" - it should be added there).
    // Scrolls the search block into view and focuses its input.
    const focusClaimSearch = () => {
      nextTick(() => {
        const proxy = instance && instance.proxy
        const wrap = proxy && proxy.$refs && proxy.$refs.searchWrap
        if (!wrap) return
        wrap.scrollIntoView({ behavior: 'smooth', block: 'start' })
        const input = wrap.querySelector('input')
        if (input) input.focus()
      })
    }


    // Currency is LOCKED (not editable) as soon as ANY UMR in the co-lead
    // came from the system - that system UMR establishes the shared currency for
    // the whole co-lead (one claim = one currency), no matter whether it was
    // added first or after a manual row. Currency only stays editable while the
    // co-lead has NO system UMR at all (every row manual). Mirrors the
    // Add-Missing-UMR modal's intent.
    const currencyLocked = computed(
      () => rows.value.some((row) => row.matched === true)
    )

    // The currency shared across the co-lead, taken from the first SYSTEM UMR
    // (matched row). Passed to the Add-Missing-UMR modal so a newly added manual
    // UMR prefills + locks its currency to match, even when the system UMR was
    // added before this manual one.
    const sharedSystemCurrency = computed(() => {
      const sys = rows.value.find((row) => row.matched === true && row.currency_type)
      return sys ? sys.currency_type : ''
    })

    // Editing is MODAL-ONLY: the row's pencil re-opens the Add Missing UMR modal
    // pre-filled with that row, rather than turning the table cells into inputs.
    // Only manual rows have a pencil - a matched (LIQ) row's values come from the
    // system key and aren't editable.
    const openEditModal = (row, index) => {
      const proxy = instance && instance.proxy
      const modal = proxy && proxy.$refs && proxy.$refs.addMissingUmrModal
      if (modal && modal.openForEdit) modal.openForEdit(row, index)
    }

    // The modal emits `update` (instead of `save`) when it was opened for an
    // existing row. Replace that row in place, preserving everything the modal
    // doesn't own - share %, funding toggle, matched flag, any extra brokers
    // beyond the primary, and the per-key claim-detail/loss-fund fields.
    const onUpdateKey = ({ index, row }) => {
      const next = rows.value.slice()
      const existing = next[index]
      if (!existing) return

      const restBrokers = Array.isArray(existing.broker) ? existing.broker.slice(1) : []
      const incoming = Array.isArray(row.broker) ? row.broker : (row.broker ? [row.broker] : [])

      next[index] = {
        ...existing,
        ...row,
        // Keep any secondary brokers added via the chips UI; the modal only
        // edits the primary one.
        broker: incoming.length ? [...incoming, ...restBrokers] : existing.broker,
        // The modal never changes these - guard against it blanking them.
        share_percentage: existing.share_percentage,
        funding_required: existing.funding_required,
        matched: existing.matched
      }
      emit('input', next)
    }

    // Close the broker popup when clicking anywhere outside it.
    const onDocClickForBroker = (event) => {
      const proxy = instance && instance.proxy
      const wrap = proxy && proxy.$refs && proxy.$refs.brokerPopupWrap
      const el = Array.isArray(wrap) ? wrap[0] : wrap
      if (el && !el.contains(event.target)) {
        closeBrokerPopup()
      }
    }

    const closeBrokerPopup = () => {
      newBroker.value = ''
      addingBrokerFor.value = null
      document.removeEventListener('mousedown', onDocClickForBroker, true)
    }
    const cancelAddBroker = () => closeBrokerPopup()

    const startAddBroker = (index) => {
      newBroker.value = ''
      addingBrokerFor.value = index
      // Focus the search box and start listening for outside clicks.
      nextTick(() => {
        const proxy = instance && instance.proxy
        const input = proxy && proxy.$refs && proxy.$refs.brokerSearchInput
        const el = Array.isArray(input) ? input[0] : input
        if (el && el.focus) el.focus()
        document.addEventListener('mousedown', onDocClickForBroker, true)
      })
    }

    const addBrokerValue = (index, value) => {
      const name = String(value || '').trim()
      if (name) {
        const current = brokerList(rows.value[index])
        // Avoid case-insensitive duplicates.
        const exists = current.some((b) => String(b).trim().toLowerCase() === name.toLowerCase())
        if (!exists) writeBrokers(index, [...current, name])
      }
      closeBrokerPopup()
    }

    const confirmAddBroker = (index) => {
      addBrokerValue(index, newBroker.value)
    }

    // Picking a suggestion from the system broker list.
    const selectBrokerSuggestion = (index, name) => {
      addBrokerValue(index, name)
    }

    onBeforeUnmount(() => {
      document.removeEventListener('mousedown', onDocClickForBroker, true)
    })

    const removeBroker = (index, brokerIndex) => {
      const current = brokerList(rows.value[index])
      const next = current.slice()
      next.splice(brokerIndex, 1)
      writeBrokers(index, next)
    }

    // Top-of-step "Claim Reference" search: free-text match across the identity
    // fields (see CO_LEAD_SEARCH_FIELDS) of the once-fetched bulk cache
    // (flattened to one row per key+claim), entirely client-side - no network
    // call per keystroke.
    const searchQuery = ref('')

    // The search index ({ rows, blobs }) is built ONCE by the parent when the
    // bulk cache is fetched, then passed in - so it survives leaving/returning
    // to this step (this component is v-if'd, so destroyed/recreated) without
    // being rebuilt over ~400k rows each time. By the time the user types,
    // searching is just `blob.includes(query)` per row.

    // Debounced so a large cache doesn't get re-scanned on every single
    // keystroke while the user is still typing - searchQuery itself stays
    // instant (bound directly to the input) so what's displayed never lags.
    const debouncedSearchQuery = ref('')
    const updateDebouncedSearchQuery = debounce((value) => { debouncedSearchQuery.value = value }, 250)
    watch(searchQuery, (value) => {
      updateDebouncedSearchQuery(value)
      // Clearing/editing the search dismisses a stale claim-ref error so it
      // doesn't linger after the user clears the box or moves on.
      if (!String(value || '').trim()) {
        claimRefError.value = ''
      }
    })

    // Close the search results when clicking anywhere outside the search block.
    // Clearing the query is what hides the results (v-if="searchQuery"), and
    // multi-select keeps them open otherwise. The listener is only attached
    // while a query (and therefore the results panel) is present.
    const closeSearch = () => {
      searchQuery.value = ''
      updateDebouncedSearchQuery.cancel()
      debouncedSearchQuery.value = ''
    }
    const onDocClickForSearch = (event) => {
      const proxy = instance && instance.proxy
      const el = proxy && proxy.$refs && proxy.$refs.searchWrap
      if (el && !el.contains(event.target)) {
        closeSearch()
      }
    }
    watch(searchQuery, (value) => {
      const active = !!String(value || '').trim()
      document.removeEventListener('mousedown', onDocClickForSearch, true)
      if (active) {
        document.addEventListener('mousedown', onDocClickForSearch, true)
      }
    })
    onBeforeUnmount(() => {
      document.removeEventListener('mousedown', onDocClickForSearch, true)
    })

    // Bdx key for a search result, matching what selectResult builds - used to
    // hide already-added UMRs from the results so multi-select doesn't re-offer
    // a key that's already in the table.
    const bdxKeyOfResult = (result) =>
      result.umr_rc_cc_sn ||
      [result.umr, result.risk_code, result.cat_code, result.section_no, result.currency_type, result.year_of_account].join('_')

    const searchResults = computed(() => {
      const query = debouncedSearchQuery.value.trim().toLowerCase()
      if (!query) return []
      const added = new Set(existingBdxKeys.value.map((k) => String(k || '').trim().toLowerCase()))
      // This box is explicitly the "Claim Reference" search and a co-lead cash
      // call is for ONE claim, so match on the CLAIM REF field only - not the
      // whole searchable blob (umr/broker/underwriter/insured/etc.). Matching
      // the blob let a query like "12" pull in an unrelated UMR whose broker or
      // UMR string merely contained "12" but whose claim ref differed, which the
      // auto-add below then rejected with a confusing "must share claim ref"
      // error for a UMR the user never chose. Once a claim ref is locked, only
      // that exact claim ref can match, so nothing off-claim is ever offered.
      const locked = lockedClaimRef.value ? lockedClaimRef.value.toLowerCase() : ''
      const { rows } = props.searchIndex
      const results = []
      for (let i = 0; i < rows.length; i += 1) {
        const claimRef = String(rows[i].claim_ref || '').trim().toLowerCase()
        if (!claimRef) continue
        // Always show system UMRs matching what the user typed (substring on the
        // claim ref), EVEN when a claim ref is already locked - so the user can
        // still see what exists in the system. Adding a result whose claim ref
        // differs from the lock is what's blocked (in onAddKey / selectResult),
        // not the searching/showing of it.
        if (!claimRef.includes(query)) continue
        const key = String(bdxKeyOfResult(rows[i]) || '').trim().toLowerCase()
        if (added.has(key)) continue
        results.push(rows[i])
        // Cap high enough to auto-add ALL UMRs for a claim ref (a claim rarely
        // spans more than a handful of keys); guards against a pathological
        // broad match adding thousands.
        if (results.length >= 200) break
      }
      return results
    })

    // ---- Pagination for the Claim Reference / UMR search results ----
    // A broad claim-ref match can return many UMRs; page them so the list stays
    // readable and the paging control sits cleanly below the table (same
    // b-pagination pattern the rest of the platform uses).
    const SEARCH_PAGE_SIZE = 6
    const searchPage = ref(1)
    // Reset to page 1 whenever the (debounced) query changes, so a new search
    // never lands the user on an out-of-range page.
    watch(debouncedSearchQuery, () => { searchPage.value = 1 })
    // Selecting a key removes it from the results (Select adds one key at a
    // time and the search stays open), so the list can shrink under the user -
    // keep the page in range so they never land on an empty last page.
    watch(() => searchResults.value.length, (len) => {
      const pages = Math.max(1, Math.ceil(len / SEARCH_PAGE_SIZE))
      if (searchPage.value > pages) searchPage.value = pages
    })
    const pagedSearchResults = computed(() => {
      const start = (searchPage.value - 1) * SEARCH_PAGE_SIZE
      return searchResults.value.slice(start, start + SEARCH_PAGE_SIZE)
    })

    const duplicateError = ref(false)

    // Selecting a search result adds it straight to the table below - no
    // separate review/confirm step. Funding Required/Share % default to
    // ON/0 and are edited afterwards inline in the table itself (same
    // columns every other row already has), not here.
    // Build the table-row payload for a search result.
    const resultToRow = (result) => {
      const bdxKey = result.umr_rc_cc_sn ||
        [result.umr, result.risk_code, result.cat_code, result.section_no, result.currency_type, result.year_of_account].join('_')
      return {
        bdx_key: [bdxKey],
        umr: result.umr,
        rc: result.risk_code,
        cc: result.cat_code,
        sn: result.section_no,
        currency_type: result.currency_type,
        year_of_account: result.year_of_account,
        claimref: result.claim_ref ? [result.claim_ref] : [],
        tpa: result.tpa || '',
        underwriter_lead: result.underwriter || '',
        broker: result.broker ? [result.broker] : [],
        coverholder: result.coverholder || '',
        insured_name: result.insured_name || '',
        date_of_loss: result.date_of_loss || null,
        date_claim_made: result.date_claim_made || null,
        // Shown (read-only) in the co-lead Step 3 summary card. NAIC is also an
        // editable shared field there. Read whichever key name the bulk payload
        // uses; fall back to blank.
        date_recent_report_period: result.date_recent_report_period || result.recent_report_date || result.Risk_Effective_Date || null,
        naic_code: result.naic_code || result.NAIC_Code || '',
        // Claim country/state can arrive under a few different key names
        // depending on the bulk endpoint's serializer (loss_country vs
        // Claim_Country vs claim_country), so read whichever is present so the
        // co-lead Claim Details country prefills from the first system UMR.
        loss_country: result.loss_country || result.Claim_Country || result.claim_country || '',
        loss_state: result.loss_state || result.Claim_State || result.claim_state || '',
        loss_sl_state: '',
        // Cause of Loss is shared across the whole co-lead (one claim). Prefill
        // it from the claim data when Lincoln's payload includes it (under any
        // of these key names); otherwise it stays blank and editable on step 3.
        cause_of_loss: result.cause_of_loss || result.Cause_of_Loss || result.causeOfLoss || '',
        share_percentage: 0,
        funding_required: true,
        matched: true
      }
    }

    const claimRefOfResult = (result) =>
      String((result && result.claim_ref) || '').trim()

    // LIQ-2414: Select adds ONLY the clicked LimeIQ Key. A claim ref can sit
    // under several keys of the same UMR (different sections, risk codes...),
    // and the user picks exactly which of them belong on this cash call - so
    // each key is selected on its own. (Previously one click added every key
    // sharing the claim ref, e.g. all 13 sections of a UMR.)
    // The search stays open after a selection so further keys can be added;
    // a key already in the table drops out of the results (see searchResults).
    const selectResult = (result) => {
      duplicateError.value = false
      const targetClaimRef = claimRefOfResult(result)

      // Every co-lead UMR must share one claim ref - the first row sets it.
      const locked = rows.value.length ? claimRefOf(rows.value[0]) : ''
      if (locked && targetClaimRef !== locked) {
        claimRefError.value =
          `All co-lead UMRs must share the same Claim Ref (${locked}). ` +
          `This UMR's Claim Ref (${targetClaimRef || '—'}) is different and was not added.`
        return
      }
      claimRefError.value = ''

      const row = resultToRow(result)
      const keyLower = String(row.bdx_key[0] || '').trim().toLowerCase()
      const alreadyAdded = existingBdxKeys.value.some(
        (k) => String(k || '').trim().toLowerCase() === keyLower
      )
      if (alreadyAdded) {
        duplicateError.value = true
        return
      }

      emit('input', [...rows.value, row])
    }

    // NOTE: auto-add-on-type was removed. Typing used to instantly sweep every
    // match into the table and clear the search box, so the user couldn't see
    // what they typed or control what was added. The search now just DISPLAYS
    // matching LimeIQ Keys; clicking SELECT on a row adds that one key.

    return {
      rows,
      existingBdxKeys,
      shareTotal,
      emitUpdate,
      onAddKey,
      removeRow,
      searchQuery,
      searchResults,
      bdxKeyOfResult,
      pagedSearchResults,
      searchPage,
      searchPageSize: SEARCH_PAGE_SIZE,
      duplicateError,
      claimRefError,
      lockedClaimRef,
      firstRecord,
      isCarriedPrimaryBroker,
      underwriterOptions,
      brokerOptions,
      selectResult,
      focusClaimSearch,
      currencyLocked,
      sharedSystemCurrency,
      openEditModal,
      onUpdateKey,
      addingBrokerFor,
      newBroker,
      brokerList,
      brokerSuggestions,
      startAddBroker,
      confirmAddBroker,
      cancelAddBroker,
      selectBrokerSuggestion,
      removeBroker
    }
  },
  computed: {
    isTpaUser() {
      return this.userCompanyRole === 'TPA'
    },
    tpaCompanyName() {
      return this.isTpaUser && this.currentUser.company ? this.currentUser.company.role_value : ''
    }
  }
})
</script>

<style scoped>
.co-lead-search {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 14px 16px;
}

.co-lead-search__results {
  background-color: #fff;
  border-radius: 6px;
  padding: 8px;
  /* Fixed layout + full width keeps the results table inside the modal.
     Without this the table sizes to its content and the trailing Select
     button column gets clipped at the right edge. */
  table-layout: fixed;
  width: 100%;
  max-width: 100%;
}
/* b-table-simple wraps itself in .table-responsive, which is the element that
   actually overflows; pin it to the container too. */
.co-lead-search >>> .table-responsive {
  max-width: 100%;
}
/* Pagination sits in its own full-width row BELOW the results table, right-
   aligned, with clear space above/below so its numbers never overlap the table
   rows or the "+ Add missing UMR" link beneath. */
.co-lead-search__pagination {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  margin: 8px 0 4px;
}
.co-lead-search__pagination >>> .pagination {
  margin-bottom: 0;
}
/* Empty-state / lock message row: left-align it under the first column instead
   of drifting to the right of the spanned cell. */
.co-lead-search__empty-cell {
  text-align: left !important;
}
/* Long UMRs / broker names must wrap rather than force the table wider. With
   table-layout:fixed, cells only wrap when the columns have explicit widths -
   without them the long first-column UMR overflowed and overlapped the Claim Ref
   column. `vertical-align: top` keeps a wrapped multi-line UMR aligned with its
   row's other single-line cells. */
.co-lead-search__results >>> th,
.co-lead-search__results >>> td {
  /* The global .table td/th rule (_table.scss) sets nowrap, which defeats the
     wrapping below and lets a long LimeIQ Key run into the Claim Ref column. */
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  vertical-align: top;
}
/* Explicit column widths so `table-layout: fixed` has proportions to honour and
   a long LimeIQ Key wraps INSIDE its own column (it is widest; the rest are
   narrower). 1=LimeIQ Key, 2=Claim Ref, 3=Underwriter, 4=Broker, 5=Select. */
.co-lead-search__results >>> th:nth-child(1),
.co-lead-search__results >>> td:nth-child(1) { width: 34%; }
.co-lead-search__results >>> th:nth-child(2),
.co-lead-search__results >>> td:nth-child(2) { width: 20%; }
.co-lead-search__results >>> th:nth-child(3),
.co-lead-search__results >>> td:nth-child(3) { width: 18%; }
.co-lead-search__results >>> th:nth-child(4),
.co-lead-search__results >>> td:nth-child(4) { width: 16%; }
/* Last column holds the Select button - give it just what it needs. */
.co-lead-search__results >>> th:last-child,
.co-lead-search__results >>> td:last-child {
  width: 12%;
  text-align: right;
  white-space: nowrap;
}

/* Search messages: shown at the top of the results, always visible. */
.co-lead-search__msg {
  font-size: 13px;
  border-radius: 6px;
  padding: 8px 12px;
}
/* Claim-ref mismatch: brand green (matches Search/Select), not alarming red. */
.co-lead-search__msg--brand {
  color: #fff;
  background-color: #1c4a50;
  font-weight: 600;
}
/* Passive hint: soft brand tint. */
.co-lead-search__msg--brand-soft {
  color: #1c4a50;
  background-color: #eef4f4;
  border-left: 3px solid #1c4a50;
}
/* Hard duplicate error stays red. */
.co-lead-search__msg--error {
  color: #f5365c;
  background-color: #fde8ec;
  font-weight: 600;
}

.co-lead-umrs__heading {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8898aa;
  margin-bottom: 10px;
}

/* Per-row broker chips + inline autocomplete */
.co-lead-brokers {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
}

.co-lead-broker-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 4px 2px 9px;
  border-radius: 12px;
  background-color: #e8f0f0;
  color: #1c4a50;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}

.co-lead-broker-chip__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 15px;
  height: 15px;
  border: none;
  border-radius: 50%;
  background: rgba(28, 74, 80, 0.12);
  color: #1c4a50;
  font-size: 12px;
  line-height: 1;
  padding: 0;
  cursor: pointer;
  transition: background-color 0.12s ease, color 0.12s ease;
}

.co-lead-broker-chip__remove:hover {
  background: #dc3545;
  color: #fff;
}

.co-lead-broker-add__btn {
  display: inline-flex;
  align-items: center;
  border: 1px dashed #b8c7c9;
  border-radius: 12px;
  background: transparent;
  color: #1c4a50;
  font-weight: 600;
  font-size: 11px;
  line-height: 1.4;
  cursor: pointer;
  padding: 2px 9px;
  transition: background-color 0.12s ease, border-color 0.12s ease;
}

.co-lead-broker-add__btn:hover {
  background: #e8f0f0;
  border-color: #1c4a50;
}

.co-lead-broker-add {
  display: inline-block;
}

.co-lead-broker-add__btn--active {
  background: #e8f0f0;
  border-style: solid;
  border-color: #1c4a50;
}

/* The popup panel: opens UPWARD (this cell sits low in the card). A fixed
   search bar sits at the top; the broker list scrolls below it. */
.co-lead-broker-pop {
  position: absolute;
  z-index: 40;
  bottom: calc(100% + 4px);
  left: 0;
  width: 220px;
  background-color: #fff;
  border: 1px solid #e3e8ec;
  border-radius: 8px;
  box-shadow: 0 -6px 18px rgba(0, 0, 0, 0.16);
  overflow: hidden;
}

.co-lead-broker-pop__search {
  display: block;
  width: 100%;
  font-size: 12px;
  padding: 7px 10px;
  border: none;
  border-bottom: 1px solid #eef1f3;
  outline: none;
}

.co-lead-broker-pop__search::placeholder {
  color: #9aa8b0;
}

.co-lead-broker-pop__list {
  margin: 0;
  padding: 4px;
  list-style: none;
  max-height: 200px;
  overflow-y: auto;
}

.co-lead-broker-pop__list li {
  padding: 6px 10px;
  font-size: 12px;
  color: #32325d;
  cursor: pointer;
  white-space: nowrap;
  border-radius: 6px;
  transition: background-color 0.1s ease, color 0.1s ease;
}

.co-lead-broker-pop__list li:hover {
  background-color: #e8f0f0;
  color: #1c4a50;
}

.co-lead-broker-pop__empty {
  color: #8898aa;
  cursor: default;
  white-space: normal;
}

.co-lead-broker-pop__empty:hover {
  background: transparent;
  color: #8898aa;
}

.co-lead-umrs__type-badge {
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  background-color: #1c4a50;
  color: #fff;
}

.co-lead-umrs__type-badge--manual {
  background-color: #8898aa;
}

.co-lead-umrs__table thead th {
  font-size: 9px;
}

.co-lead-umrs__table tbody td {
  font-size: 10px;
}
/* Let the table grow to its natural (content) width rather than squishing to
   fit the wrapper - that's what makes it overflow and trigger the horizontal
   scrollbar in edit mode instead of cramming the columns. */

/* Only the co-lead table scrolls horizontally (when the edit inputs make it
   wider than the modal), so the far-right action column stays reachable. The
   wizard's .form-container keeps overflow-x: hidden, so OTHER steps never show a
   horizontal scrollbar - it appears ONLY here, and only when the table is wide
   (i.e. a row is in inline-edit mode). Constrain the wrapper to the container
   width; BootstrapVue's inner .table-responsive is the actual scroller. */
/* No horizontal scrollbar - let the table's responsive wrapper size to the
   modal; edit inputs are kept compact so the row fits. */
.co-lead-umrs__table-wrap {
  max-width: 100%;
  /* Every selected UMR is in this one table (no pagination), so it scrolls
     VERTICALLY once it has more than 5 rows - a typical co-lead's 4-5 UMRs fit
     without a scrollbar, and a large one (e.g. 13 sections of one UMR) no
     longer pushes Share total and Save and Next off the screen.
     Sized from a measured table: header + 8 rows = 397px, i.e. ~46px a row.
     5 rows ~260px fit; 6 rows ~306px scroll, with half the 6th row showing as
     a cue that there is more below.
     Nothing in the table opens a dropdown any more (inline editing was
     removed), so the scroll container clips nothing.
     Tooltips inside the table MUST use v-b-tooltip...viewport: BootstrapVue's
     default boundary is the nearest scroll parent - this box - so with only
     1-2 rows there is no room above a button and the tooltip is pushed down
     on top of it (the "Remove" tooltip covered the x).
     No horizontal scroll: the table uses table-layout:fixed with column widths
     summing to 100%, so every column (incl. Share % and remove) fits. */
  max-height: 290px;
  overflow-y: auto;
  overflow-x: hidden;
}
/* Keep the column headings visible while the rows scroll underneath. */
.co-lead-umrs__table >>> thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
}
.co-lead-umrs__table-wrap >>> .table-responsive {
  overflow: visible !important;
}
/* The row that owns an open dropdown must paint above its siblings. */
.co-lead-umrs__table >>> tr,
.co-lead-umrs__table >>> td {
  position: relative;
}
/* Fixed layout makes the 12 columns divide the available width instead of each
   sizing to its content. Without this the edit-mode inputs push the row wider
   than the modal and .table-responsive shows a horizontal scrollbar. */
.co-lead-umrs__table {
  table-layout: fixed;
  width: 100%;
}
/* Explicit column widths. With table-layout:fixed the browser divides the
   width using the FIRST row only, so the narrow header labels let the wide
   edit-mode inputs shove the trailing action column (the confirm tick) past
   the modal edge, where overflow-x:hidden clips it. Pinning every column so
   the percentages total 100% keeps the tick on-screen in both modes.
   13 columns: Type, UMR, Section #, Risk Code, CAT Code, CCY, Claim Ref,
   Underwriter, Broker, Funding Required, Share %, actions, remove. */
.co-lead-umrs__table >>> th:nth-child(1),
.co-lead-umrs__table >>> td:nth-child(1)  { width: 6%; }   /* Type */
.co-lead-umrs__table >>> th:nth-child(2),
.co-lead-umrs__table >>> td:nth-child(2)  { width: 12%; }  /* LimeIQ Key (wraps) */
/* LimeIQ Key cell only: override the global nowrap so the long key wraps inside
   its column instead of spilling into Section #. */
.co-lead-umrs__table >>> td:nth-child(2) {
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}
.co-lead-umrs__table >>> th:nth-child(3),
.co-lead-umrs__table >>> td:nth-child(3)  { width: 7%; }   /* Section # */
.co-lead-umrs__table >>> th:nth-child(4),
.co-lead-umrs__table >>> td:nth-child(4)  { width: 7%; }   /* Risk Code */
.co-lead-umrs__table >>> th:nth-child(5),
.co-lead-umrs__table >>> td:nth-child(5)  { width: 7%; }   /* CAT Code */
.co-lead-umrs__table >>> th:nth-child(6),
.co-lead-umrs__table >>> td:nth-child(6)  { width: 6%; }   /* CCY */
.co-lead-umrs__table >>> th:nth-child(7),
.co-lead-umrs__table >>> td:nth-child(7)  { width: 9%; }   /* Claim Ref */
.co-lead-umrs__table >>> th:nth-child(8),
.co-lead-umrs__table >>> td:nth-child(8)  { width: 10%; }  /* Underwriter */
.co-lead-umrs__table >>> th:nth-child(9),
.co-lead-umrs__table >>> td:nth-child(9)  { width: 11%; }  /* Broker */
.co-lead-umrs__table >>> th:nth-child(10),
.co-lead-umrs__table >>> td:nth-child(10) { width: 8%; }   /* Funding Required */
.co-lead-umrs__table >>> th:nth-child(11),
.co-lead-umrs__table >>> td:nth-child(11) { width: 9%; }   /* Share % */
.co-lead-umrs__table >>> th:nth-child(12),
.co-lead-umrs__table >>> td:nth-child(12) { width: 4%; }   /* confirm tick */
.co-lead-umrs__table >>> th:nth-child(13),
.co-lead-umrs__table >>> td:nth-child(13) { width: 4%; }   /* remove */
/* The trailing action cells must never wrap or shrink their icons out of view. */
.co-lead-umrs__table >>> td:nth-child(12),
.co-lead-umrs__table >>> td.co-lead-umrs__remove-cell {
  white-space: nowrap;
  text-align: center;
  padding-left: 2px;
  padding-right: 2px;
}
/* With fixed columns, long values (UMR, Claim Ref) must wrap rather than push
   the column wider. */
.co-lead-umrs__table >>> td {
  word-break: break-word;
}
/* Compact cell padding so the row's edit inputs fit without needing to scroll,
   and tighter vertical padding to trim the empty space between rows. */
.co-lead-umrs__table >>> td {
  padding-left: 6px;
  padding-right: 6px;
  padding-top: 4px;
  padding-bottom: 4px;
}
.co-lead-umrs__table >>> th {
  padding-top: 4px;
  padding-bottom: 4px;
}

.co-lead-umrs__share-total {
  font-weight: 700;
}

/* When shares reach 100%, use the same teal/green as the Save and Next button
   (#1c4a50) instead of Bootstrap's brighter text-success. */
.co-lead-umrs__share-total--complete {
  color: #1c4a50;
}

.co-lead-umrs__share {
  width: 78px;
  min-width: 78px;
}
/* Keep the Share % number input a stable width so it doesn't shrink when a row
   enters edit mode and the other cells' inputs claim horizontal space. Wide
   enough for a full "100.00" PLUS the number input's spinner arrows, which eat
   ~16px on the right - at 60px a typed "50.00" was clipped. */
.co-lead-umrs__share >>> input {
  width: 78px;
  min-width: 78px;
}

/* Action cell: lay the icons out right-aligned so the remove (x) is ALWAYS in
   the same far-right position across every row - whether or not the row also has
   an edit/save pencil beside it. Without this, a row with only the x centred it
   differently from a row with pencil + x, so the x's didn't line up. */
.co-lead-umrs__remove-cell {
  vertical-align: middle;
  white-space: nowrap;
  text-align: right;
}
.co-lead-umrs__remove-cell > button {
  vertical-align: middle;
}

.co-lead-umrs__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;                /* fixed footprint so the x column is consistent */
  padding: 0;
  border: none;
  background: transparent;
  color: #dc3545;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  transition: color 0.15s ease;
}

.co-lead-umrs__remove:hover {
  color: #a71d2a;
}

/* Edit/save (pencil / check) button - a fixed-width slot BEFORE the x so the x
   stays put whether or not this button is present. */
.co-lead-umrs__edit {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  padding: 0;
  margin-right: 8px;
  border: none;
  background: transparent;
  color: #1c4a50;
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  transition: color 0.15s ease;
}

.co-lead-umrs__edit:hover {
  color: #123236;
}
/* Empty stand-in that reserves the pencil's slot on LIQ rows (no pencil), so
   the x lines up with the x on Manual rows (pencil + x). Same width + margin as
   the pencil button; not interactive. */
.co-lead-umrs__edit--placeholder {
  visibility: hidden;
  pointer-events: none;
}

/* Inline-edit cell inputs: compact, fill the cell width. */
/* Keep every cell vertically centred so plain inputs and autocomplete inputs
   sit on the same line when a row is in edit mode. */
.co-lead-umrs__table >>> td {
  vertical-align: middle;
}

.co-lead-inline-input {
  display: block;
  width: 100%;
  min-width: 0;
  height: auto;
  margin: 0;
  padding: 3px 6px;
  font-size: 13px;
  line-height: 1.4;
  border: 1px solid #cfd8dc;
  border-radius: 4px;
}
.co-lead-inline-input:focus {
  outline: none;
  border-color: #1c4a50;
  box-shadow: 0 0 0 2px rgba(28, 74, 80, 0.15);
}

/* A required inline field left blank on Save: red border so the user sees which
   cell to fill. Works for both the plain inputs and the autocomplete wrapper
   (targets the inner control for the latter). */
.co-lead-inline-input--invalid,
.co-lead-inline-input--invalid:focus {
  border-color: #f5365c !important;
  box-shadow: 0 0 0 2px rgba(245, 54, 92, 0.15) !important;
}
.co-lead-inline-autocomplete.co-lead-inline-input--invalid >>> .form-control,
.co-lead-inline-autocomplete.co-lead-inline-input--invalid >>> input {
  border-color: #f5365c !important;
  box-shadow: 0 0 0 2px rgba(245, 54, 92, 0.15) !important;
}

/* Inline-edit type-ahead cells: make the autocomplete render EXACTLY like the
   plain inline inputs (same height/padding/border) so the row cells line up and
   the row height doesn't jump. The autocomplete wraps a BaseInput -> b-input
   (.form-control), plus a label/validation wrapper; strip all of that and size
   the inner control to match .co-lead-inline-input. */
.co-lead-inline-autocomplete {
  width: 100%;
  min-width: 70px;
}
/* Kill BaseInput's label, form-group margin and validation feedback spacing. */
.co-lead-inline-autocomplete >>> label {
  display: none;
}
.co-lead-inline-autocomplete >>> .form-group,
.co-lead-inline-autocomplete >>> .validation-provider {
  margin-bottom: 0;
}
/* The actual text input inside: match the compact plain-input look exactly. */
.co-lead-inline-autocomplete >>> .form-control,
.co-lead-inline-autocomplete >>> input {
  height: auto;
  width: 100%;
  padding: 3px 6px;
  font-size: 13px;
  line-height: 1.4;
  border: 1px solid #cfd8dc;
  border-radius: 4px;
}
.co-lead-inline-autocomplete >>> .form-control:focus,
.co-lead-inline-autocomplete >>> input:focus {
  border-color: #1c4a50;
  box-shadow: 0 0 0 2px rgba(28, 74, 80, 0.15);
}

/* Inline required-field validation message for the row being edited. */
.co-lead-umrs__error-row td {
  border-top: none;
  padding-top: 2px;
}
.co-lead-umrs__error-cell {
  color: #f5365c;
  font-size: 11px;
  font-weight: 600;
}
</style>
